import { useState, useRef, useLayoutEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import NativeSelect from '@mui/material/NativeSelect';
import InputLabel from '@mui/material/InputLabel';
import CircularProgress from "@mui/material/CircularProgress";

import AuditRow from './AuditRow';
import { auditRowKey } from './auditRowKey';
import { bucketAssignmentCategory, isWorkflowComplete } from '../utils/assignmentStatus';
import { colSx, stickyHeadSx, AUDIT_TABLE_MIN_WIDTH } from './auditColumns';
import * as React from 'react';

// Small breathing room below the grid so the container's scrollbar isn't flush
// against the window edge.
const AUDIT_TABLE_BOTTOM_GAP = 8;

export default function AuditList(props) {

  const propsSeasonArray = props.seasonsArray;
  const seasonDataForQueries = props.seasonDataForQueries;

  const handleUpdateLocationDimFacility = props.handleUpdateLocationDimFacility;
  const seasonsArray = [...propsSeasonArray];

  const deleteSqlRow = props.deleteSqlRow;
  const addOrUpdateSQLRow = props.addOrUpdateSQLRow;
  const updateInAsyncronousManner = props.updateInAsyncronousManner;
  const comcodes = props.comcodes;
  const bucketsByKey = props.bucketsByKey || {};

  const [acceptedFilter, setAcceptedFilter] = useState("All");
  const [addedFilter, setAddedFilter] = useState("All");
  const [TrackingYearFilter, setTrackingYearFilter] = useState("All");
  const [TrackingSeasonFilter, setTrackingSeasonFilter] = useState("All");
  const [comCodeFilter, setComCodeFilter] = useState("All");
  const [comIDFilter, setComIDFilter] = useState("All");
  const [facilityNameFilter, setFacilityNameFilter] = useState("All");
  const [ProgramTypeFilter, setProgramTypeFilter] = useState("All");
  const [bucketStatusFilter, setBucketStatusFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");

  // --- Synchronized top horizontal scrollbar ---
  // A thin scroller above the grid mirrors the TableContainer's horizontal scroll,
  // so an auditor can scroll columns without hunting for the bottom bar on a tall
  // grid. The table renders ONCE inside the TableContainer (the real/bottom scroller);
  // the top strip is a cosmetic spacer whose width tracks the table's scrollWidth.
  // Both scrollLeft positions are kept in sync. No page-level scroll; header/body
  // alignment and expanded rows are untouched (they live inside the one table).
  const tableRef = useRef(null);
  const topScrollRef = useRef(null);
  const bottomScrollRef = useRef(null);
  const syncingRef = useRef(false);
  // spacerWidth drives the top scroller's inner width. Defaults to the table's
  // known minimum so the bar is present on first paint and can never collapse to 0.
  const [spacerWidth, setSpacerWidth] = useState(AUDIT_TABLE_MIN_WIDTH);
  // Native horizontal scrollbar thickness, measured once, used as the strip's
  // explicit height so the real scrollbar is fully visible (no custom styling).
  const [scrollbarH, setScrollbarH] = useState(15);
  // Live cap for the grid's scroll container: the viewport height remaining below
  // the table's top edge. Undefined (no cap) until measured — the layout effect
  // below runs before first paint, so this initial value is never shown.
  const [maxTableH, setMaxTableH] = useState(undefined);

  // Measure the native scrollbar thickness once (probe an always-scrolling div).
  useLayoutEffect(() => {
    const probe = document.createElement('div');
    probe.style.cssText = 'overflow-x:scroll;overflow-y:hidden;position:absolute;top:-9999px;width:100px;height:100px;';
    document.body.appendChild(probe);
    const h = probe.offsetHeight - probe.clientHeight;
    document.body.removeChild(probe);
    if (h > 0) setScrollbarH(h);
  }, []);

  // Mirror the audit table's ACTUAL rendered width into the top spacer so the top
  // scroller overflows exactly like the TableContainer. rAF so the read happens
  // after layout; a ResizeObserver on the TABLE catches any width change; re-runs
  // when the pulled data changes. Falls back to AUDIT_TABLE_MIN_WIDTH so the
  // spacer can never collapse to zero.
  useLayoutEffect(() => {
    const t = tableRef.current;
    if (!t) return;
    let raf = 0;
    const measure = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const w = Math.ceil(t.getBoundingClientRect().width);
        setSpacerWidth(w > 0 ? w : AUDIT_TABLE_MIN_WIDTH);
      });
    };
    measure();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(t);
    }
    return () => { cancelAnimationFrame(raf); if (ro) ro.disconnect(); };
  }, [seasonsArray.length]);

  // Cap the grid's scroll container to the viewport space below its own top edge,
  // so the table body scrolls internally and the page itself does not overflow —
  // one scrollbar, not two. The top edge depends only on the chrome above (not on
  // the table's own height), so measuring it and re-applying maxHeight can't feed
  // back into a loop. Re-measures on window resize and when the row count changes
  // (which can add/remove the summary row and shift the table's top).
  useLayoutEffect(() => {
    const el = bottomScrollRef.current;
    if (!el) return;
    let raf = 0;
    const measure = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const top = el.getBoundingClientRect().top;
        const h = Math.round(window.innerHeight - top - AUDIT_TABLE_BOTTOM_GAP);
        // Floor so a short viewport can never collapse the grid to nothing.
        setMaxTableH(Math.max(240, h));
      });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', measure); };
  }, [seasonsArray.length]);

  // One-shot guard: writing scrollLeft fires the other element's onScroll; the flag
  // absorbs that echo so the two never ping-pong.
  const syncScroll = (from, to) => {
    if (!from || !to) return;
    if (syncingRef.current) { syncingRef.current = false; return; }
    syncingRef.current = true;
    to.scrollLeft = from.scrollLeft;
  };
  const onTopScroll = () => syncScroll(topScrollRef.current, bottomScrollRef.current);
  const onBottomScroll = () => syncScroll(bottomScrollRef.current, topScrollRef.current);

  const handleSelectAccepted = (e) => {
    setAcceptedFilter(e.target.value)
    // updateInAsyncronousManner();
  }

  const handleSelectAdded = (e) => {
    setAddedFilter(e.target.value)
    // updateInAsyncronousManner();

  }

  const handleSelectTrackingSeason = (e) => {
    setTrackingSeasonFilter(e.target.value)
    // updateInAsyncronousManner();

  }

  const handleSelectTrackingYear = (e) => {
    setTrackingYearFilter(e.target.value)
    // updateInAsyncronousManner();

  }

  const handleSelectComCode = (e) => {
    setComCodeFilter(e.target.value)
    // updateInAsyncronousManner();
  }

  const handleSelectComID = (e) => {
    setComIDFilter(e.target.value)
    // updateInAsyncronousManner();
  }

  const handleSelectFacilityName = (e) => {
    setFacilityNameFilter(e.target.value)
    // updateInAsyncronousManner();
  }

  const handleSelectProgramType = (e) => {
    setProgramTypeFilter(e.target.value)
    // updateInAsyncronousManner();
  }

  const handleSelectBucketStatus = (e) => {
    setBucketStatusFilter(e.target.value)
  }

  const handleSelectStatus = (e) => {
    setStatusFilter(e.target.value)
  }

  function filterBy(seasonObj) {
    if (
      ((acceptedFilter !== "All") && ((acceptedFilter === 'true' && !seasonObj.accepted) || (acceptedFilter === 'false' && seasonObj.accepted)))
      || ((addedFilter !== "All") && ((addedFilter === 'true' && !seasonObj.added) || (addedFilter === 'false' && seasonObj.added)))
      || ((TrackingYearFilter !== "All") && ((TrackingYearFilter !== seasonObj.TrackingYear)))
      || ((TrackingSeasonFilter !== "All") && ((TrackingSeasonFilter !== seasonObj.TrackingSeason)))
      || ((comCodeFilter !== "All") && ((comCodeFilter !== seasonObj.comCode)))
      || ((comIDFilter !== "All") && ((comIDFilter !== seasonObj.comID)))
      || ((facilityNameFilter !== "All") && ((facilityNameFilter !== seasonObj.facilityName)))
      || ((ProgramTypeFilter !== "All") && ((Number(ProgramTypeFilter) !== Number(seasonObj.ProgramType))))
      || ((bucketStatusFilter !== "All") && (bucketAssignmentCategory(seasonObj, bucketsByKey) !== bucketStatusFilter))
      // Status work queue: exclude rows that don't match the selected Status.
      // "complete" -> drop rows that aren't complete; "needswork" -> drop complete rows.
      || ((statusFilter !== "All") && ((statusFilter === 'complete') !== isWorkflowComplete(seasonObj.accepted, seasonObj.season_sequence)))
    ) {
      return false
    }
    else return true
  }
  const filteredSeasonsArray = seasonsArray.filter(filterBy);

  const newSeasonComponentArray = filteredSeasonsArray.map((seasonObj) => {

  let locationNotes = undefined;
  let dim_facilityID = undefined;

  const seasonComId = String(seasonObj.comID ?? "").replace(/,/g, "").trim();

  const index = comcodes.findIndex((element) => {
    const comcodeComId = String(element.comID ?? "").replace(/,/g, "").trim();
    return comcodeComId === seasonComId;
  });

  if (index !== -1) {
    const locationIndex = (comcodes[index].locations || []).findIndex((element) => {
      return element.location === seasonObj.facilityName;
    });

    if (locationIndex !== -1) {
      locationNotes = comcodes[index].locations[locationIndex].locationLKSINotes;
      dim_facilityID = comcodes[index].locations[locationIndex].dim_facilityID;
    }
  }

    // const priorSeasonArray = seasonDataForQueries.filter((element) => {


    //   if (element.comID == seasonObj.comID && element.facilityName === seasonObj.facilityName && element.TrackingYear == seasonObj.TrackingYear - 1) {
    //     return true;
    //   }

    //   return false;
    // })

    const priorSeasonArray = [];

    const transformed = (
      <AuditRow seasonData={seasonObj} key={auditRowKey(seasonObj)} seasonDataForQueries={seasonDataForQueries}
        locationNotes={locationNotes} dim_facilityID={dim_facilityID} priorSeasonArray={priorSeasonArray}
        handleUpdateLocationDimFacility={handleUpdateLocationDimFacility} updateInAsyncronousManner={updateInAsyncronousManner}
        deleteSqlRow={deleteSqlRow} addOrUpdateSQLRow={addOrUpdateSQLRow} bucketsByKey={bucketsByKey}
      />
    )

    return transformed;

  })

  console.log(seasonDataForQueries);



  const newSeasonComponentArrayRefresh = [...newSeasonComponentArray]

  function compareComcode(a, b) {
    if (a.key < b.key) {
      return -1;
    }
    if (a.key > b.key) {
      return 1;
    }
    return 0;
  }

  function compareComID(a, b) {
    let firstComID = a.com
    if (Number(a.key) < Number(b.key)) {
      return -1;
    }
    if (Number(a.key) > Number(b.key)) {
      return 1;
    }
    return 0;
  }

  function compareFacilityName(a, b) {
    if (a.key < b.key) {
      return -1;
    }
    if (a.key > b.key) {
      return 1;
    }
    return 0;
  }

  const uniqueComCodeFilters = [];
  const comCodeFilterOptions = filteredSeasonsArray.map((seasonObj) => {
    const { comCode } = seasonObj;
    const optionElement = <option value={comCode} key={comCode}>{comCode}</option>
    return optionElement
  }).filter((optionElement) => {
    const isDuplicate = uniqueComCodeFilters.includes(optionElement.key);
    if (!isDuplicate) {
      uniqueComCodeFilters.push(optionElement.key);
      return true;
    }
    return false;
  }).sort(compareComcode);

  const uniqueComIDFilters = [];
  const comIDFilterOptions = filteredSeasonsArray.map((seasonObj) => {
    const { comID } = seasonObj;
    const optionElement = <option value={comID} key={comID}>{comID}</option>
    return optionElement
  }).filter((optionElement) => {
    const isDuplicate = uniqueComIDFilters.includes(optionElement.key);
    if (!isDuplicate) {
      uniqueComIDFilters.push(optionElement.key);
      return true;
    }
    return false;
  }).sort(compareComID);

  const uniqueFacilityFilterNames = [];
  const facilityNameFilterOptions = filteredSeasonsArray.map((seasonObj) => {
    const { facilityName, sKey } = seasonObj;
    const optionElement = <option value={facilityName} key={facilityName}>{facilityName}</option>
    return optionElement
  }).filter((optionElement) => {
    const isDuplicate = uniqueFacilityFilterNames.includes(optionElement.key);
    if (!isDuplicate) {
      uniqueFacilityFilterNames.push(optionElement.key);
      return true;
    }
    return false;
  }).sort(compareFacilityName);

  const uniqueTrackingYearFilters = [];
  const TrackingYearFilterOptions = filteredSeasonsArray.map((seasonObj) => {
    const { TrackingYear } = seasonObj;
    const optionElement = <option value={TrackingYear} key={TrackingYear}>{TrackingYear}</option>
    return optionElement
  }).filter((optionElement) => {
    const isDuplicate = uniqueTrackingYearFilters.includes(optionElement.key);
    if (!isDuplicate) {
      uniqueTrackingYearFilters.push(optionElement.key);
      return true;
    }
    return false;
  });

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
      border: 0,
    },
  }));

  const headerRow = (
    <StyledTableRow>
      <TableCell sx={stickyHeadSx('expander')} />
      <TableCell align="center" sx={stickyHeadSx('comCode')}>
        <InputLabel variant="standard">Com Code</InputLabel>
        <NativeSelect name="comCode" id="comCode" defaultValue={comCodeFilter} onChange={handleSelectComCode}>
          <option value='All'>All</option>
          {comCodeFilterOptions}
        </NativeSelect>
      </TableCell>
      <TableCell align="center" sx={stickyHeadSx('facility')}>
        <InputLabel variant="standard">Facility</InputLabel>
        <NativeSelect name="facilityName" id="facilityName" defaultValue={facilityNameFilter} onChange={handleSelectFacilityName}>
          <option value='All'>All</option>
          {facilityNameFilterOptions}
        </NativeSelect>
      </TableCell>
      <TableCell align="center" sx={colSx('seasonId')}>
        <InputLabel variant="standard">Season ID</InputLabel>
        _
      </TableCell>
      <TableCell align="center" sx={colSx('seasonName')}>
        <InputLabel variant="standard">Season Name</InputLabel>
        _
      </TableCell>
      <TableCell align="center" sx={colSx('startDate')}>
        <InputLabel variant="standard">Start Date</InputLabel>
        _
      </TableCell>
      <TableCell align="center" sx={colSx('endDate')}>
        <InputLabel variant="standard">End Date</InputLabel>
        _
      </TableCell>
      <TableCell align="center" sx={colSx('priorSeason')}>
        <InputLabel variant="standard">Prior Season</InputLabel>
        _
      </TableCell>
      <TableCell align="center" sx={colSx('priorYearSeason')}>
        <InputLabel variant="standard">Prior Year Season</InputLabel>
        _
      </TableCell>
      <TableCell align="center" sx={colSx('programType')}>
        <InputLabel variant="standard">Program Type</InputLabel>
        <NativeSelect defaultValue={ProgramTypeFilter} onChange={handleSelectProgramType}>
          <option value='All'>All</option>
          <option value={1}>LK</option>
          <option value={2}>SI</option>
          <option value={0}>Other</option>
        </NativeSelect>
      </TableCell>
      <TableCell align="center" sx={colSx('season')}>
        <InputLabel variant="standard">Season</InputLabel>
        <NativeSelect defaultValue={TrackingSeasonFilter} onChange={handleSelectTrackingSeason}>
          <option value='All'>All</option>
          <option value={1}>Spring</option>
          <option value={2}>Summer</option>
          <option value={3}>Fall</option>
          <option value={4}>Winter</option>
          <option value={0}>Other</option>
        </NativeSelect>
      </TableCell>
      <TableCell align="center" sx={colSx('year')}>
        <InputLabel variant="standard">Year</InputLabel>
        <NativeSelect defaultValue={TrackingYearFilter} onChange={handleSelectTrackingYear}>
          <option value='All'>All</option>
          {TrackingYearFilterOptions}
        </NativeSelect>
      </TableCell>
      <TableCell align="center" sx={colSx('lkOrSi')}>
        <InputLabel variant="standard">LK or SI</InputLabel>
        <NativeSelect name="Added" id="Added" defaultValue={addedFilter} onChange={handleSelectAdded}>
          <option value='All'>All</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </NativeSelect>
      </TableCell>
      <TableCell align="center" sx={colSx('franchiseBucket')}>
        <InputLabel variant="standard">Franchise Bucket</InputLabel>
        _
      </TableCell>
      <TableCell align="center" sx={colSx('bucketSeason')}>
        <InputLabel variant="standard">Bucket → Season</InputLabel>
        _
      </TableCell>
      <TableCell align="center" sx={colSx('bucketStatus')}>
        <InputLabel variant="standard">Bucket Status</InputLabel>
        <NativeSelect name="bucketStatus" id="bucketStatus" defaultValue={bucketStatusFilter} onChange={handleSelectBucketStatus}>
          <option value="All">All</option>
          <option value="awaiting">Awaiting bucket</option>
          <option value="assigned">Assigned</option>
          <option value="no-calendar">No calendar</option>
        </NativeSelect>
      </TableCell>
      <TableCell align="center" sx={colSx('rowSaved')}>
        <InputLabel variant="standard">Reviewed</InputLabel>
        <NativeSelect name="Accepted" id="Accepted" defaultValue={acceptedFilter} onChange={handleSelectAccepted}>
          <option value="All">All</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </NativeSelect>
      </TableCell>
      <TableCell align="center" sx={colSx('status')}>
        <InputLabel variant="standard">Audit Status</InputLabel>
        <NativeSelect name="status" id="status" defaultValue={statusFilter} onChange={handleSelectStatus}>
          <option value="All">All</option>
          <option value="needswork">Needs Work</option>
          <option value="complete">Complete</option>
        </NativeSelect>
      </TableCell>
      <TableCell align="center" sx={colSx('action')}>
        <InputLabel variant="standard">Action</InputLabel>
        _
      </TableCell>
    </StyledTableRow>
  );

  if (seasonsArray.length === 0) {
    return (
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: maxTableH }}>
          <Table stickyHeader size="small" sx={{ tableLayout: 'auto', minWidth: AUDIT_TABLE_MIN_WIDTH }}>
            <TableHead aria-label="sticky table" className="thead-dark">
              {headerRow}
            </TableHead>
          </Table>
        </TableContainer>
        <CircularProgress />
      </Paper>
    );
  }



  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Top synchronized horizontal scrollbar (mirrors the TableContainer below).
          Explicit height = the native scrollbar thickness, so the real scrollbar is
          fully visible with no browser-specific styling. */}
      <Box ref={topScrollRef} onScroll={onTopScroll}
        sx={{ overflowX: 'auto', overflowY: 'hidden', height: `${scrollbarH}px` }}>
        <Box sx={{ width: `${spacerWidth}px`, height: '1px' }} />
      </Box>
      <TableContainer ref={bottomScrollRef} onScroll={onBottomScroll} sx={{ maxHeight: maxTableH }}>
        <Table ref={tableRef} stickyHeader size="small" sx={{ tableLayout: 'auto', minWidth: AUDIT_TABLE_MIN_WIDTH }}>
          <TableHead aria-label="sticky table" className="thead-dark">
            {headerRow}
          </TableHead>
          <TableBody>
            {newSeasonComponentArrayRefresh}
          </TableBody>
        </Table>

      </TableContainer>
    </Paper>
  )
}


